// ---------------------------------------------------------------------------
// animation.ts — Ladder + mantle animation driver for the v7 wire fields.
//
// Companion to player-animator.ts (which handles standing/walking/running/
// crouching/airborne/death). Where player-animator computes a procedural
// pose from continuous velocity inputs, this driver consumes the discrete
// Protocol v7 bits — `isOnLadder`, `ladderId`, `isMantling`, `mantleDest`,
// `mantleT` — and exposes a small set of canned pose parameters that the
// renderer overlays on top of the procedural pose.
//
// Why split this out from player-animator.ts:
//   - the canned anims are state-machine driven, not velocity-derived
//   - the falling-edge "lerp pose to server position" drift correction is
//     specific to the mantle finish and would crowd player-animator
//   - the door.ts interpolator + this module + sound-designer's splash
//     hook are the three places the v7 wire bits land on the client; one
//     module per concern keeps the wiring obvious
//
// Per physics_findings.md §11.3, on `isMantling` falling edge the client
// MUST lerp the rendered position toward the server-authoritative pose
// over 2-3 frames to hide any drift. That's the same drift correction
// the existing player position prediction already does — this driver
// emits a `driftCorrection` value the renderer consumes; the actual
// position blend lives in the renderer where it has scene access.
// ---------------------------------------------------------------------------

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

/** Minimal Protocol v7 PlayerSnapshot subset this driver consumes. */
export interface ClimbAnimState {
  isOnLadder?: boolean;
  ladderId?: number;
  isMantling?: boolean;
  mantleT?: number;
  mantleDest?: { x: number; y: number; z: number };
  position: { x: number; y: number; z: number };
}

/** Output pose overlay applied on top of player-animator's procedural pose. */
export interface ClimbAnimParams {
  /** True while the canned ladder hand-over-hand anim should play. */
  isLadderClimb: boolean;
  /** Ladder rung phase, radians; advances at LADDER_PHASE_SPEED. */
  ladderPhase: number;
  /** Vertical climb amplitude — 0 when the rung phase is at rest. */
  ladderAmplitude: number;

  /** True while the canned mantle anim should play. */
  isMantle: boolean;
  /** 0..1 progress along the mantle Bezier. Source: server `mantleT`/255. */
  mantleProgress: number;
  /** Forward-lean angle (radians) — peaks mid-mantle, returns to 0 by t=1. */
  mantleLeanRad: number;

  /**
   * Drift correction blend weight for THIS frame, in [0, 1].
   * Renderer applies `displayPos = lerp(prevDisplayPos, serverPos, weight)`
   * for ~MANTLE_DRIFT_LERP_FRAMES after the falling edge of `isMantling`,
   * then weight returns to 0 and the position track is unmediated.
   * Per physics_findings.md §11.3 — same drift-correction the player
   * position predictor already runs.
   */
  driftCorrection: number;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

/** Hand-over-hand ladder phase advance — radians per second. */
const LADDER_PHASE_SPEED = 6.0;
/** Vertical climb amplitude (meters) when actively climbing. */
const LADDER_AMPLITUDE = 0.04;

/** Forward lean peak during mantle (radians, ~17°). Mid-arc body angle. */
const MANTLE_LEAN_PEAK_RAD = 0.30;

/** Number of render frames over which the falling-edge drift correction
 *  blends `displayPos` toward `serverPos`. 60 fps × 3 frames = 50 ms — well
 *  under one network tick (15.625 ms × ~3 ticks lookahead). */
export const MANTLE_DRIFT_LERP_FRAMES = 3;

/** Per-frame drift weight while the correction window is active. The
 *  remaining gap shrinks by 1/N each frame — geometric blend, not linear,
 *  so the snap is invisible in practice. */
const MANTLE_DRIFT_WEIGHT = 1 / MANTLE_DRIFT_LERP_FRAMES;

/* ------------------------------------------------------------------ */
/*  ClimbAnimator                                                      */
/* ------------------------------------------------------------------ */

/**
 * Canned ladder + mantle animation driver. One instance per remote
 * player (and one for the local player's TPP shadow if rendered).
 *
 * State machine:
 *   - `isOnLadder` true   → ladderPhase advances, isLadderClimb=true
 *   - `isMantling` true   → mantle pose tracks server `mantleT`
 *   - falling edge of `isMantling` arms a MANTLE_DRIFT_LERP_FRAMES window
 *     during which `driftCorrection` is non-zero so the renderer can lerp
 *     into the authoritative server position.
 *   - both bits false     → ladderPhase damps to nearest rest (matches
 *     the player-animator walkPhase damping pattern so the legs don't
 *     freeze mid-stride)
 */
export class ClimbAnimator {
  private ladderPhase = 0;

  // True while the previous tick was mantling — used to detect the
  // falling edge and start the drift-correction window.
  private wasMantling = false;
  private driftFramesRemaining = 0;

  /** Cached output — recomputed every update(). */
  private params: ClimbAnimParams = {
    isLadderClimb: false,
    ladderPhase: 0,
    ladderAmplitude: 0,
    isMantle: false,
    mantleProgress: 0,
    mantleLeanRad: 0,
    driftCorrection: 0,
  };

  /**
   * Advance the canned-anim state by dt seconds, given the latest
   * server snapshot for this player. Output is reachable via getParams().
   */
  update(dt: number, snap: ClimbAnimState): void {
    const onLadder = snap.isOnLadder === true;
    const mantling = snap.isMantling === true;

    // Ladder phase — advance while attached, damp toward rest otherwise.
    if (onLadder) {
      this.ladderPhase += LADDER_PHASE_SPEED * dt;
    } else {
      // Damp toward the nearest π multiple — same behaviour as the
      // player-animator walk phase damping. Keeps legs from freezing
      // mid-rung when the player dismounts.
      const nearestRest = Math.round(this.ladderPhase / Math.PI) * Math.PI;
      const blend = Math.min(1, 10 * dt);
      this.ladderPhase += (nearestRest - this.ladderPhase) * blend;
    }

    // Falling-edge mantle detection — arms the drift-correction window.
    // We arm on the tick the bit goes false, not on the tick after, so
    // the renderer starts blending immediately.
    if (this.wasMantling && !mantling) {
      this.driftFramesRemaining = MANTLE_DRIFT_LERP_FRAMES;
    }
    this.wasMantling = mantling;

    let driftCorrection = 0;
    if (this.driftFramesRemaining > 0) {
      driftCorrection = MANTLE_DRIFT_WEIGHT;
      this.driftFramesRemaining -= 1;
    }

    // Mantle progress: server quantises 0..255 over MantleDurationS.
    const t255 = snap.mantleT ?? 0;
    const progress = mantling ? Math.max(0, Math.min(1, t255 / 255)) : 0;

    // Forward lean follows a sin(πt) bell — 0 at start/end, peak mid-arc.
    const leanRad = mantling ? Math.sin(progress * Math.PI) * MANTLE_LEAN_PEAK_RAD : 0;

    this.params = {
      isLadderClimb: onLadder,
      ladderPhase: this.ladderPhase,
      ladderAmplitude: onLadder ? LADDER_AMPLITUDE : 0,
      isMantle: mantling,
      mantleProgress: progress,
      mantleLeanRad: leanRad,
      driftCorrection,
    };
  }

  /** Return the current canned-anim output. */
  getParams(): ClimbAnimParams {
    return this.params;
  }

  /** True for MANTLE_DRIFT_LERP_FRAMES frames after a mantle finishes. */
  isDriftCorrecting(): boolean {
    return this.driftFramesRemaining > 0;
  }
}

/**
 * Standalone helper for the renderer's drift-correction step. Returns the
 * lerped position for THIS frame given the previous display position, the
 * current server position, and the drift weight from the animator.
 *
 * `weight === 0` → returns serverPos (identity passthrough).
 * `weight ∈ (0, 1]` → linear interpolation; the renderer accumulates
 * across frames so the apparent blend is geometric.
 */
export function lerpDriftCorrection(
  prev: { x: number; y: number; z: number },
  server: { x: number; y: number; z: number },
  weight: number,
): { x: number; y: number; z: number } {
  if (weight <= 0) return { x: server.x, y: server.y, z: server.z };
  if (weight >= 1) return { x: server.x, y: server.y, z: server.z };
  return {
    x: prev.x + (server.x - prev.x) * weight,
    y: prev.y + (server.y - prev.y) * weight,
    z: prev.z + (server.z - prev.z) * weight,
  };
}
